import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Paper,
  Divider,
} from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useApi } from "../api";
import { trackAccountCreationConversion } from "../analytics";
import PageBreadcrumbs from "../components/Breadcrumbs";
import { useCurrentUser } from "../auth";
import {
  ORGANIZATION_EDIT_PERMISSION,
  STATS_USAGE_PERMISSION,
  BILLING_GRANT_PERMISSION,
  BILLING_SUBSCRIPTION_MANUAL_PERMISSION,
  BILLING_MANAGE_PERMISSION,
  BILLING_PURCHASE_PACK_PERMISSION,
  BILLING_PURCHASE_SUBSCRIPTION_PERMISSION,
} from "../constants/permissions";

type ActionsUsageResponse = Record<string, { available: number; avg_monthly_3m: number }>;
type ResourcesUsageResponse = Record<string, { available: number; used: number; limit: number | null }>;
type WooCommerceIntegrationStatus = {
  enabled: boolean;
  key_prefix?: string | null;
  plugin_zip_download_url: string;
  integration_key?: string | null;
};

// Lightweight section wrapper — just a border, minimal padding
const SectionCard: React.FC<{ children: React.ReactNode; sx?: object }> = ({ children, sx }) => (
  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: "background.paper", ...sx }}>
    {children}
  </Paper>
);

const TabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2.5 }}>
    {value === index && children}
  </Box>
);

// Compact usage row: label | progress bar | number [button]
const UsageRow: React.FC<{
  label: string;
  percent: number;
  barColor: string;
  rightText: string;
  showGrantButton?: boolean;
  onGrant?: () => void;
  grantLabel?: string;
}> = ({ label, percent, barColor, rightText, showGrantButton, onGrant, grantLabel }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 0.5 }}>
    <Typography variant="body2" sx={{ minWidth: 160, flexShrink: 0 }}>
      {label}
    </Typography>
    <LinearProgress
      variant="determinate"
      value={percent}
      color="inherit"
      sx={{
        flexGrow: 1,
        height: 6,
        borderRadius: 3,
        backgroundColor: "#e9ecef",
        "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 3 },
      }}
    />
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, minWidth: 80, justifyContent: "flex-end" }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {rightText}
      </Typography>
      {showGrantButton && (
        <Button size="small" variant="outlined" onClick={onGrant} sx={{ py: 0, px: 1, minHeight: 24, fontSize: "0.7rem" }}>
          {grantLabel}
        </Button>
      )}
    </Box>
  </Box>
);

const OrganisationSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApi();
  const { reloadCurrentUser, permissions, currentUser, organizationSetupRequired } = useCurrentUser();

  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [usage, setUsage] = useState<ActionsUsageResponse | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [resourcesUsage, setResourcesUsage] = useState<ResourcesUsageResponse | null>(null);
  const [resourcesError, setResourcesError] = useState<string | null>(null);

  const hasStatsPermission = useMemo(() => permissions.includes(STATS_USAGE_PERMISSION), [permissions]);
  const hasOrganizationEditPermission = useMemo(() => permissions.includes(ORGANIZATION_EDIT_PERMISSION), [permissions]);
  const hasGrantPermission = useMemo(() => permissions.includes(BILLING_GRANT_PERMISSION), [permissions]);
  const hasSubscriptionManualPermission = useMemo(() => permissions.includes(BILLING_SUBSCRIPTION_MANUAL_PERMISSION), [permissions]);
  const canAccessBillingStore = useMemo(
    () =>
      permissions.includes(BILLING_MANAGE_PERMISSION) ||
      permissions.includes(BILLING_PURCHASE_PACK_PERMISSION) ||
      permissions.includes(BILLING_PURCHASE_SUBSCRIPTION_PERMISSION),
    [permissions]
  );

  const isSetupMode = organizationSetupRequired;
  const MIN_BASELINE = 50;
  const [activeTab, setActiveTab] = useState(0);

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantActionCode, setGrantActionCode] = useState<string | null>(null);
  const [grantUnits, setGrantUnits] = useState<number>(0);
  const [grantSubmitting, setGrantSubmitting] = useState(false);
  const [usageSuccess, setUsageSuccess] = useState<string | null>(null);

  const [subOpen, setSubOpen] = useState(false);
  const [subFrom, setSubFrom] = useState<string>("");
  const [subTo, setSubTo] = useState<string>("");
  const [subSubmitting, setSubSubmitting] = useState(false);
  const [subError, setSubError] = useState<string | null>(null);

  const [woocommerceStatus, setWooCommerceStatus] = useState<WooCommerceIntegrationStatus | null>(null);
  const [woocommerceLoading, setWooCommerceLoading] = useState(false);
  const [woocommerceActionLoading, setWooCommerceActionLoading] = useState(false);
  const [woocommerceError, setWooCommerceError] = useState<string | null>(null);
  const [woocommerceSuccess, setWooCommerceSuccess] = useState<string | null>(null);
  const [oneTimeIntegrationKey, setOneTimeIntegrationKey] = useState<string | null>(null);

  const loadUsage = useCallback(async () => {
    if (!hasStatsPermission) return;
    try {
      const data = await api.get("/stats/actions-usage");
      setUsage(data || {});
      setUsageError(null);
    } catch (err) {
      console.error("Failed to load action usage", err);
      setUsageError(t("organizationSetup.usageFetchError", "Failed to load usage data."));
    }
  }, [api, hasStatsPermission, t]);

  const loadResourcesUsage = useCallback(async () => {
    if (!hasStatsPermission) return;
    try {
      const data = await api.get("/stats/resources-usage");
      setResourcesUsage(data || {});
      setResourcesError(null);
    } catch (err) {
      console.error("Failed to load resource usage", err);
      setResourcesError(t("organizationSetup.resourcesUsageFetchError", "Failed to load resource data."));
    }
  }, [api, hasStatsPermission, t]);

  const loadWooCommerceIntegration = useCallback(async () => {
    if (!hasOrganizationEditPermission || isSetupMode) return;
    setWooCommerceLoading(true);
    try {
      const data = await api.get("/organizations/me/integrations/woocommerce");
      setWooCommerceStatus(data);
      setWooCommerceError(null);
    } catch (err) {
      console.error("Failed to load WooCommerce integration", err);
      setWooCommerceError(t("organizationSetup.woocommerce.loadFailed"));
    } finally {
      setWooCommerceLoading(false);
    }
  }, [api, hasOrganizationEditPermission, isSetupMode, t]);

  useEffect(() => {
    let isActive = true;
    const loadOrganization = async () => {
      try {
        const response = await api.get("/organizations/me");
        if (!isActive || !response) return;
        if (response.name) setName(response.name);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes("404")) return;
        console.error("Failed to load organization", err);
      }
    };
    loadOrganization();
    loadUsage();
    loadResourcesUsage();
    loadWooCommerceIntegration();
    return () => { isActive = false; };
  }, [api, loadUsage, loadResourcesUsage, loadWooCommerceIntegration]);

  const handleWooCommerceGenerateKey = useCallback(async () => {
    setWooCommerceActionLoading(true);
    setWooCommerceError(null);
    setWooCommerceSuccess(null);
    try {
      const data = await api.post("/organizations/me/integrations/woocommerce/key", {});
      setWooCommerceStatus(data);
      setOneTimeIntegrationKey(data.integration_key || null);
      setWooCommerceSuccess(data.integration_key ? t("organizationSetup.woocommerce.keyGenerated") : t("organizationSetup.woocommerce.keyAlreadyExists"));
    } catch (err) {
      console.error("Failed to create WooCommerce integration key", err);
      setWooCommerceError(t("organizationSetup.woocommerce.keyGenerateFailed"));
    } finally {
      setWooCommerceActionLoading(false);
    }
  }, [api, t]);

  const handleWooCommerceRotateKey = useCallback(async () => {
    const confirmed = window.confirm(t("organizationSetup.woocommerce.rotateConfirm"));
    if (!confirmed) return;
    setWooCommerceActionLoading(true);
    setWooCommerceError(null);
    setWooCommerceSuccess(null);
    try {
      const data = await api.post("/organizations/me/integrations/woocommerce/key/rotate", {});
      setWooCommerceStatus(data);
      setOneTimeIntegrationKey(data.integration_key || null);
      setWooCommerceSuccess(t("organizationSetup.woocommerce.keyRotated"));
    } catch (err) {
      console.error("Failed to rotate WooCommerce integration key", err);
      setWooCommerceError(t("organizationSetup.woocommerce.keyRotateFailed"));
    } finally {
      setWooCommerceActionLoading(false);
    }
  }, [api, t]);

  const handleWooCommerceDownload = useCallback(async () => {
    const downloadPath = woocommerceStatus?.plugin_zip_download_url || "/organizations/me/integrations/woocommerce/plugin.zip";
    setWooCommerceActionLoading(true);
    setWooCommerceError(null);
    try {
      const response = await api.getResponse(downloadPath);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = "valid-labels-clp.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Failed to download WooCommerce plugin", err);
      setWooCommerceError(t("organizationSetup.woocommerce.downloadFailed"));
    } finally {
      setWooCommerceActionLoading(false);
    }
  }, [api, t, woocommerceStatus?.plugin_zip_download_url]);

  const handleCopyIntegrationKey = useCallback(async () => {
    if (!oneTimeIntegrationKey) return;
    await navigator.clipboard.writeText(oneTimeIntegrationKey);
    setWooCommerceSuccess(t("organizationSetup.woocommerce.keyCopied"));
  }, [oneTimeIntegrationKey, t]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSuccessMessage(null);
    setErrorMessage(null);
    try {
      const response = await api.put("/organizations/me", { name: name.trim() });
      setSuccessMessage(t("organizationSetup.success"));
      if (response?.name) setName(response.name);
      if (isSetupMode) {
        trackAccountCreationConversion(response?.id);
      }
      await reloadCurrentUser();
      if (isSetupMode) navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Failed to save organization", err);
      setErrorMessage(t("organizationSetup.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const tabs: Array<{ label: string; key: string }> = [
    { label: t("organizationSetup.tabs.general", "General"), key: "general" },
  ];
  if (!isSetupMode && hasOrganizationEditPermission) {
    tabs.push({ label: t("organizationSetup.tabs.integrations", "Integrations"), key: "integrations" });
  }
  if (!isSetupMode && hasStatsPermission) {
    tabs.push({ label: t("organizationSetup.tabs.usage", "Usage"), key: "usage" });
  }

  // Setup mode — centered card, unchanged
  if (isSetupMode) {
    return (
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ maxWidth: 480, mx: "auto", px: 2, py: { xs: 6, md: 10 }, minHeight: "calc(100vh - 140px)", display: "flex", alignItems: "center" }}
      >
        <Stack
          spacing={3}
          sx={{ width: "100%", textAlign: "center", p: { xs: 3, md: 5 }, borderRadius: 3, backgroundColor: "background.paper", boxShadow: "0 18px 48px rgba(15,23,42,0.08)" }}
        >
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }} gutterBottom>
              {t("organizationSetup.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, mx: "auto" }}>
              {t("organizationSetup.description")}
            </Typography>
          </Box>
          {currentUser?.referral_partner_name && (
            <Alert severity="info" sx={{ textAlign: "left" }}>
              {t("organizationSetup.referralInvite", { partnerName: currentUser.referral_partner_name })}
            </Alert>
          )}
          {successMessage && <Alert severity="success" onClose={() => setSuccessMessage(null)}>{successMessage}</Alert>}
          {errorMessage && <Alert severity="error" onClose={() => setErrorMessage(null)}>{errorMessage}</Alert>}
          <TextField
            label={t("organizationSetup.nameLabel")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <Button type="submit" variant="contained" disabled={isSubmitting || !name.trim()} size="small">
            {isSubmitting ? t("organizationSetup.saving") : t("organizationSetup.submit")}
          </Button>
        </Stack>
      </Box>
    );
  }

  // Normal mode — full-width layout matching other pages
  return (
    <Box sx={{ pb: 6 }}>
      <PageBreadcrumbs
        items={[
          { label: t("nav.dashboard"), path: "/dashboard" },
          { label: t("organizationSetup.title") },
        ]}
      />

      <Box sx={{ mt: 1.5, mb: 2.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          {t("organizationSetup.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {t("organizationSetup.description")}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} sx={{ fontSize: "0.8rem" }} />
          ))}
        </Tabs>
      </Box>

      {/* ── General ── */}
      <TabPanel value={activeTab} index={0}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          {successMessage && <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ py: 0.5 }}>{successMessage}</Alert>}
          {errorMessage && <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ py: 0.5 }}>{errorMessage}</Alert>}

          <SectionCard>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
              {t("organizationSetup.nameLabel")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {t("organizationSetup.namePlaceholder")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", maxWidth: 480 }}>
              <TextField
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                size="small"
                sx={{ flexGrow: 1 }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !name.trim()}
                size="small"
                sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                {isSubmitting ? t("organizationSetup.saving") : t("organizationSetup.submit")}
              </Button>
            </Box>
          </SectionCard>

          {canAccessBillingStore && (
            <SectionCard>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("organizationSetup.billingStoreTitle", "Subscriptions & limits")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("organizationSetup.billingStoreDescription", "Manage your plan, limits, and payments.")}
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  size="small"
                  endIcon={<ArrowForwardIcon fontSize="small" />}
                  onClick={() => navigate("/billing/store")}
                  sx={{ flexShrink: 0, ml: 2 }}
                >
                  {t("organizationSetup.billingStoreButton", "Go to store")}
                </Button>
              </Box>
            </SectionCard>
          )}
        </Stack>
      </TabPanel>

      {/* ── Integrations ── */}
      {tabs.some((t) => t.key === "integrations") && (
        <TabPanel value={activeTab} index={tabs.findIndex((t) => t.key === "integrations")}>
          <Stack spacing={2}>
            {woocommerceError && <Alert severity="error" onClose={() => setWooCommerceError(null)} sx={{ py: 0.5 }}>{woocommerceError}</Alert>}
            {woocommerceSuccess && <Alert severity="success" onClose={() => setWooCommerceSuccess(null)} sx={{ py: 0.5 }}>{woocommerceSuccess}</Alert>}

            <SectionCard>
              {woocommerceLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

              {/* Header */}
              <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t("organizationSetup.woocommerce.title")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("organizationSetup.woocommerce.description")}
                  </Typography>
                </Box>
                {/* Status badge — dot + label only, no key prefix */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: 2, flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: woocommerceStatus?.enabled ? "success.main" : "text.disabled",
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" color={woocommerceStatus?.enabled ? "success.dark" : "text.secondary"}>
                    {woocommerceStatus?.enabled ? "Active" : "Not configured"}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* One-time key reveal */}
              {oneTimeIntegrationKey && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "warning.light",
                    backgroundColor: "warning.50",
                    maxWidth: 540,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 1, color: "warning.dark" }}>
                    {t("organizationSetup.woocommerce.oneTimeTitle")}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box
                      sx={{
                        flexGrow: 1,
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.75,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        color: "text.primary",
                        userSelect: "all",
                      }}
                    >
                      {oneTimeIntegrationKey}
                    </Box>
                    <Button size="small" variant="outlined" onClick={handleCopyIntegrationKey} sx={{ flexShrink: 0 }}>
                      {t("organizationSetup.woocommerce.copyKey")}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Action buttons */}
              <Stack direction="row" spacing={1} sx={{ mb: 2.5 }}>
                <Button size="small" variant="contained" onClick={handleWooCommerceGenerateKey} disabled={woocommerceActionLoading}>
                  {t("organizationSetup.woocommerce.generateKey")}
                </Button>
                <Button size="small" variant="outlined" onClick={handleWooCommerceRotateKey} disabled={woocommerceActionLoading || !woocommerceStatus?.enabled}>
                  {t("organizationSetup.woocommerce.rotateKey")}
                </Button>
                <Button size="small" variant="outlined" onClick={handleWooCommerceDownload} disabled={woocommerceActionLoading}>
                  {t("organizationSetup.woocommerce.downloadPlugin")}
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Installation steps */}
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", display: "block", mb: 1.25 }}>
                {t("organizationSetup.woocommerce.installTitle")}
              </Typography>
              <Stack spacing={0.75}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <Box key={n} sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "primary.main",
                        minWidth: 16,
                        flexShrink: 0,
                        lineHeight: 1.6,
                      }}
                    >
                      {n}.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      {t(`organizationSetup.woocommerce.installStep${n}`)}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </SectionCard>
          </Stack>
        </TabPanel>
      )}

      {/* ── Usage ── */}
      {tabs.some((t) => t.key === "usage") && (
        <TabPanel value={activeTab} index={tabs.findIndex((t) => t.key === "usage")}>
          <Stack spacing={2}>
            {hasSubscriptionManualPermission && (
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button size="small" variant="outlined" onClick={() => { setSubFrom(""); setSubTo(""); setSubError(null); setSubOpen(true); }}>
                  {t("organizationSetup.subscriptionManualOpen", "Add manual subscription")}
                </Button>
              </Box>
            )}

            {usageSuccess && <Alert severity="success" onClose={() => setUsageSuccess(null)} sx={{ py: 0.5 }}>{usageSuccess}</Alert>}

            {/* Action usage */}
            <SectionCard>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", display: "block", mb: 1.5 }}>
                {t("organizationSetup.usageSectionTitle", "Action usage")}
              </Typography>
              {usageError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>{usageError}</Alert>}
              {usage && Object.keys(usage).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t("organizationSetup.usageEmpty", "No usage data.")}
                </Typography>
              )}
              {usage && (
                <Stack spacing={1.5} divider={<Divider />}>
                  {Object.entries(usage).map(([code, stats]) => {
                    const label = t(`actions.${code}`, code);
                    const baseline = Math.max(stats.avg_monthly_3m || 0, MIN_BASELINE);
                    const ratio = baseline > 0 ? Math.min(1, (stats.available || 0) / baseline) : 0;
                    const percent = Math.max(0, Math.min(100, Math.floor(ratio * 100)));
                    const barColor = percent <= 20 ? "#d32f2f" : percent < 50 ? "#ed6c02" : "#2e7d32";
                    const rightText = (stats.available || 0) > baseline ? `${stats.available}` : `${stats.available} / ${baseline}`;
                    return (
                      <UsageRow
                        key={code}
                        label={label}
                        percent={percent}
                        barColor={barColor}
                        rightText={rightText}
                        showGrantButton={hasGrantPermission}
                        onGrant={() => { setGrantActionCode(code); setGrantUnits(0); setGrantOpen(true); }}
                        grantLabel={t("organizationSetup.usageGrantOpen", "Add")}
                      />
                    );
                  })}
                </Stack>
              )}
            </SectionCard>

            {/* Resource usage */}
            <SectionCard>
              <Typography variant="caption" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.07em", color: "text.secondary", display: "block", mb: 1.5 }}>
                {t("organizationSetup.resourcesUsageSectionTitle", "Resource usage")}
              </Typography>
              {resourcesError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>{resourcesError}</Alert>}
              {resourcesUsage && Object.keys(resourcesUsage).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  {t("organizationSetup.resourcesUsageEmpty", "No resource data.")}
                </Typography>
              )}
              {resourcesUsage && (
                <Stack spacing={1.5} divider={<Divider />}>
                  {Object.entries(resourcesUsage).map(([code, stats]) => {
                    const label = t(`resources.${code}`, code);
                    const unlimited = stats.available < 0 || stats.limit == null;
                    const percent = unlimited ? 100 : stats.limit && stats.limit > 0 ? Math.max(0, Math.min(100, Math.floor((stats.used / stats.limit) * 100))) : 0;
                    const usageRatio = unlimited ? 0 : stats.limit && stats.limit > 0 ? stats.used / stats.limit : 0;
                    const barColor = unlimited ? "#2e7d32" : usageRatio >= 0.9 ? "#d32f2f" : usageRatio >= 0.5 ? "#ed6c02" : "#2e7d32";
                    const rightText = unlimited ? `${stats.used} / ∞` : `${stats.used} / ${stats.limit}`;
                    return <UsageRow key={code} label={label} percent={percent} barColor={barColor} rightText={rightText} />;
                  })}
                </Stack>
              )}
            </SectionCard>
          </Stack>
        </TabPanel>
      )}

      {/* Grant Credits Dialog */}
      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>{t("organizationSetup.usageGrantTitle", "Add credits")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {t("organizationSetup.usageGrantFor", "Action")}: <strong>{grantActionCode}</strong>
            </Typography>
            <TextField
              type="number"
              label={t("organizationSetup.usageGrantUnits", "Credits")}
              value={grantUnits}
              onChange={(e) => setGrantUnits(Number(e.target.value))}
              fullWidth
              size="small"
              inputProps={{ min: 1 }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setGrantOpen(false)} disabled={grantSubmitting}>
            {t("organizationSetup.cancel", "Cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={grantSubmitting || !grantActionCode || !grantUnits || grantUnits <= 0}
            onClick={async () => {
              if (!grantActionCode || !grantUnits || grantUnits <= 0) return;
              setGrantSubmitting(true);
              try {
                await api.post("/organizations/me/grants", { action_code: grantActionCode, units: grantUnits });
                setGrantOpen(false);
                setUsageSuccess(t("organizationSetup.usageGrantSuccess", "Credits added."));
                await loadUsage();
              } catch (err) {
                console.error("Failed to grant credits", err);
                setUsageError(t("organizationSetup.usageGrantError", "Failed to add credits."));
              } finally {
                setGrantSubmitting(false);
              }
            }}
          >
            {grantSubmitting ? t("organizationSetup.adding", "Adding...") : t("organizationSetup.usageGrantConfirm", "Add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Subscription Dialog */}
      <Dialog open={subOpen} onClose={() => setSubOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>{t("organizationSetup.subscriptionManualTitle", "Add manual subscription")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField
              type="datetime-local"
              label={t("organizationSetup.subscriptionManualFrom", "Active from")}
              value={subFrom}
              onChange={(e) => setSubFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <TextField
              type="datetime-local"
              label={t("organizationSetup.subscriptionManualTo", "Active to")}
              value={subTo}
              onChange={(e) => setSubTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              error={Boolean(subFrom && subTo && new Date(subTo) <= new Date(subFrom))}
              helperText={subFrom && subTo && new Date(subTo) <= new Date(subFrom) ? t("organizationSetup.invalidDateRange", "End date must be after start date.") : " "}
            />
            {subError && <Alert severity="error" sx={{ py: 0.5 }}>{subError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setSubOpen(false)} disabled={subSubmitting}>
            {t("organizationSetup.cancel", "Cancel")}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={subSubmitting || !subFrom || !subTo || new Date(subTo) <= new Date(subFrom)}
            onClick={async () => {
              setSubError(null);
              if (!subFrom || !subTo) { setSubError(t("organizationSetup.invalidDateRange", "End date must be after start date.")); return; }
              const fromDate = new Date(subFrom);
              const toDate = new Date(subTo);
              if (!(toDate > fromDate)) { setSubError(t("organizationSetup.invalidDateRange", "End date must be after start date.")); return; }
              setSubSubmitting(true);
              try {
                await api.post("/organizations/me/subscriptions/manual", { active_from: fromDate.toISOString(), active_to: toDate.toISOString() });
                setSubOpen(false);
                setUsageSuccess(t("organizationSetup.subscriptionManualSuccess", "Subscription added."));
                await loadUsage();
              } catch (err) {
                console.error("Failed to add manual subscription", err);
                setSubError(t("organizationSetup.subscriptionManualError", "Failed to add subscription."));
              } finally {
                setSubSubmitting(false);
              }
            }}
          >
            {subSubmitting ? t("organizationSetup.adding", "Adding...") : t("organizationSetup.subscriptionManualConfirm", "Add")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganisationSettings;
